import React from 'react';
import { Link } from 'react-router-dom';
import { SITE } from '../../config/site';

// =============================================================================
// Text of the legal pages. One place to change: the pages read it from here.
// The contact email comes from src/config/site.js (SITE.email).
// =============================================================================
export const LEGAL_UPDATED = '18 October 2026';

const Mail = () => <a href={`mailto:${SITE.email}`}>{SITE.email}</a>;

export const LEGAL_PAGES = [
    { path: '/privacy', label: 'Privacy Policy' },
    { path: '/terms', label: 'Terms of Service' },
    { path: '/data-deletion', label: 'Data Deletion' },
];

export const LEGAL = {
    privacy: {
        title: 'Privacy Policy',
        description: 'How Hirsta collects, uses and protects the data of salons, their staff and their clients.',
        summary: [
            'Hirsta is software that salons use for billing, clients, staff, memberships and WhatsApp messages.',
            'A salon owns the data of its clients. We keep it safe and use it only to run the service for that salon.',
            'We never sell personal data, and we never see or store card numbers.',
            <>To see, correct or delete your data, write to <Mail />.</>,
        ],
        sections: [
            {
                title: 'Who we are',
                body: (
                    <>
                        <p>Hirsta (app.hirsta.com) is a salon billing and management service run by HIRSTA, Noida, Uttar Pradesh, India (&ldquo;Hirsta&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;).</p>
                        <p>This policy covers two kinds of people:</p>
                        <ul>
                            <li><strong>Salon users:</strong> owners, managers and staff who log in to Hirsta. For their account data we decide how it is used.</li>
                            <li><strong>Salon clients:</strong> people whose details a salon records in Hirsta. The salon decides how their data is used; we process it for the salon, on its instructions.</li>
                        </ul>
                    </>
                ),
            },
            {
                title: 'What we collect',
                body: (
                    <>
                        <h3>From salon users</h3>
                        <ul>
                            <li>Name, mobile number, email address, username and password (stored only in scrambled, hashed form).</li>
                            <li>Business details: salon name, branches, address, GSTIN, logo, price list, staff records and attendance.</li>
                            <li>Login records: IP address, device and browser, time of login.</li>
                        </ul>
                        <h3>From salon clients (entered by the salon)</h3>
                        <ul>
                            <li>Name, mobile number, email, gender, birthday and anniversary if the salon adds them, family members and notes.</li>
                            <li>Visits, bills, services taken, staff who served, packages, prepaid cards, memberships, dues and advance balance.</li>
                            <li>WhatsApp messages the salon sends, their delivery status, and replies such as STOP.</li>
                        </ul>
                        <h3>Payments</h3>
                        <p>Wallet recharges are paid through Razorpay. Razorpay handles card, UPI and bank details; we receive only the payment result and reference number.</p>
                    </>
                ),
            },
            {
                title: 'How we use it',
                body: (
                    <>
                        <ul>
                            <li>To run the service: bills and invoices, client history, memberships, staff and payroll, reports.</li>
                            <li>To send the messages a salon chooses: bills, reminders and offers on WhatsApp, and invoice links.</li>
                            <li>To manage wallet recharges and the cost of messages.</li>
                            <li>To keep accounts secure, prevent misuse and fix problems.</li>
                            <li>To give support, and to meet tax and legal duties.</li>
                        </ul>
                        <p>We do not use salon client data for our own marketing, and we do not sell it.</p>
                    </>
                ),
            },
            {
                title: 'WhatsApp messages',
                body: (
                    <>
                        <p>Hirsta sends WhatsApp messages through the official WhatsApp Business Platform by Meta. To deliver a message we share the client&rsquo;s mobile number and the message content with Meta, and Meta tells us whether it was delivered or read.</p>
                        <p>Salons must send offers only to clients who have agreed to get them. Anyone who replies <strong>STOP</strong> is added to the salon&rsquo;s stop list and gets no more offers from that salon.</p>
                    </>
                ),
            },
            {
                title: 'Who we share data with',
                body: (
                    <>
                        <p>Only with the services we need to run Hirsta, each bound to protect the data:</p>
                        <ul>
                            <li>Hosting and database: Render and MongoDB Atlas.</li>
                            <li>Messages: Meta (WhatsApp Business Platform).</li>
                            <li>Payments: Razorpay.</li>
                            <li>Fonts on our pages: Google Fonts.</li>
                        </ul>
                        <p>We may also share data when the law requires it, for example on a valid order from a government authority.</p>
                    </>
                ),
            },
            {
                title: 'How we protect it',
                body: (
                    <>
                        <ul>
                            <li>All traffic is encrypted (HTTPS).</li>
                            <li>Each salon sees only its own data, and each user sees only what their role allows.</li>
                            <li>Passwords are stored hashed; secrets and keys are kept out of the code.</li>
                        </ul>
                        <p data-note>Our service providers may store data on servers outside India. We use providers that protect data to recognised standards.</p>
                    </>
                ),
            },
            {
                title: 'How long we keep it',
                body: <p>We keep data while the salon&rsquo;s account is active. When an account is closed, we delete its data within 90 days, except records that tax law requires us or the salon to keep (such as GST invoices), which we keep only as long as that law requires.</p>,
            },
            {
                title: 'Your rights',
                body: (
                    <>
                        <p>Under India&rsquo;s Digital Personal Data Protection Act, 2023, you can ask to:</p>
                        <ul>
                            <li>see the personal data we hold about you;</li>
                            <li>correct or complete it;</li>
                            <li>delete it, or withdraw consent you gave;</li>
                            <li>raise a complaint.</li>
                        </ul>
                        <p>Salon clients: please contact your salon first, as it controls your data. You can also write to us at <Mail /> and we will pass your request to the salon and help it act. See our <Link to="/data-deletion">data deletion page</Link>.</p>
                    </>
                ),
            },
            {
                title: 'Children',
                body: <p>Hirsta is not meant for users under 18. A salon may record a minor as a client only with a parent&rsquo;s or guardian&rsquo;s consent.</p>,
            },
            {
                title: 'Changes to this policy',
                body: <p>If we change this policy, we update the date at the top. For important changes we also tell salon owners in the app or by email.</p>,
            },
            {
                title: 'Grievance officer',
                body: <p>For any privacy question or complaint, write to our grievance officer at <Mail />. Address: HIRSTA, Noida, Uttar Pradesh, India.</p>,
            },
        ],
    },

    terms: {
        title: 'Terms of Service',
        description: 'The terms salons agree to when they use Hirsta salon software.',
        summary: [
            'These terms apply to salons and their staff who use Hirsta.',
            'Your salon owns its data; you are responsible for having your clients\u2019 permission to store it and to message them.',
            'Wallet balance is prepaid and used for WhatsApp messages.',
            'Indian law applies.',
        ],
        sections: [
            { title: 'The service', body: <p>Hirsta (app.hirsta.com) gives salons tools for billing, clients, staff, inventory, memberships, reports and WhatsApp messages. By creating an account or using Hirsta, the salon (&ldquo;you&rdquo;) agrees to these terms.</p> },
            {
                title: 'Accounts',
                body: (
                    <ul>
                        <li>Keep your login details safe and do not share them. You are responsible for what is done from your accounts.</li>
                        <li>The owner decides which staff get access and what they can do.</li>
                        <li>Give correct business details, including GSTIN where you use it on invoices.</li>
                    </ul>
                ),
            },
            {
                title: 'Your data and your clients',
                body: (
                    <ul>
                        <li>Your salon owns the data it enters. We use it only to run Hirsta for you, as set out in our <Link to="/privacy">Privacy Policy</Link>.</li>
                        <li>You must have your clients&rsquo; permission to store their details and to send them messages, and you must follow the law that applies to you.</li>
                        <li>You can export your data, and ask us to delete it, at any time.</li>
                    </ul>
                ),
            },
            {
                title: 'WhatsApp messages',
                body: (
                    <ul>
                        <li>Messages are sent through the WhatsApp Business Platform, and you must follow Meta&rsquo;s WhatsApp Business policies.</li>
                        <li>Send offers only to clients who agreed to get them. Clients who reply STOP are not sent offers again.</li>
                        <li>Meta decides whether a message is delivered. A message Meta does not deliver is not charged to your wallet.</li>
                    </ul>
                ),
            },
            {
                title: 'Plans, wallet and payments',
                body: (
                    <ul>
                        <li>Plan prices are shown before you pay and include GST where stated.</li>
                        <li>Wallet balance is prepaid and used for message costs. It is not refundable as cash, except where a payment was taken by mistake.</li>
                        <li>Payments are processed by Razorpay.</li>
                    </ul>
                ),
            },
            { title: 'Fair use', body: <p>Do not use Hirsta to send spam, to break the law, to harm others, or to try to get into data or systems that are not yours.</p> },
            { title: 'Availability', body: <p>We work to keep Hirsta running at all times, but there may be short breaks for upkeep or for reasons outside our control. We will tell you in advance about planned breaks where we can.</p> },
            { title: 'Liability', body: <p>Hirsta is provided as it is. To the extent the law allows, our total liability for any claim is limited to the amount you paid us in the three months before the claim. We are not liable for indirect losses such as lost profit.</p> },
            { title: 'Ending the service', body: <p>You can stop using Hirsta at any time. We may suspend an account that breaks these terms, after telling you why where we can. After an account closes, data is handled as described in the Privacy Policy.</p> },
            { title: 'Law and disputes', body: <p>These terms are governed by the laws of India. Courts in Gautam Buddh Nagar (Noida), Uttar Pradesh have jurisdiction.</p> },
            { title: 'Changes', body: <p>If we change these terms, we update the date at the top and tell salon owners about important changes before they apply.</p> },
        ],
    },

    deletion: {
        title: 'Data Deletion',
        description: 'How salons and their clients can have their data deleted from Hirsta, and how to stop WhatsApp messages.',
        summary: [
            'Salon owners can have their whole account and all its data deleted.',
            'Salon clients can ask for their own details to be deleted.',
            'To stop WhatsApp offers from a salon, reply STOP to its message.',
            <>Requests go to <Mail /> and are completed within 30 days.</>,
        ],
        sections: [
            {
                title: 'If you own a salon account',
                body: (
                    <>
                        <p>Write to <Mail /> from the email address registered with your account, with:</p>
                        <ul>
                            <li>your salon name and registered mobile number;</li>
                            <li>whether you want the whole account deleted, or only certain data.</li>
                        </ul>
                        <p>We confirm your identity, then delete the account, its branches, staff logins, client records, bills, memberships and message history within 30 days. Before we delete, we can send you an export of your data if you ask.</p>
                        <p data-note>Records that tax law requires to be kept, such as GST invoices, are kept only for as long as that law requires and are then deleted.</p>
                    </>
                ),
            },
            {
                title: 'If you are a salon client',
                body: (
                    <>
                        <p>Your salon controls your details. Ask the salon to delete them, or write to <Mail /> with:</p>
                        <ul>
                            <li>your name and mobile number;</li>
                            <li>the name and city of the salon.</li>
                        </ul>
                        <p>We pass your request to the salon and help it remove your details within 30 days.</p>
                    </>
                ),
            },
            { title: 'Stop WhatsApp messages', body: <p>Reply <strong>STOP</strong> to any offer message from a salon. You are added to that salon&rsquo;s stop list at once and get no more offers from it.</p> },
            { title: 'Facebook and Meta login', body: <p>Salons that connect WhatsApp through &ldquo;Continue with Facebook&rdquo; can disconnect Hirsta at any time in their Facebook settings (Settings and privacy &gt; Business integrations). We then delete the WhatsApp keys we stored for that salon.</p> },
        ],
    },
};
